'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { AlertCircle, Info } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { z } from 'zod'

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from '@/components/ui/'
import { useForm } from '@/hooks/useForm'
import {
  trackLoginCodeSendFailed,
  trackLoginCodeSent,
} from '@/lib/analytics/events'
import { generateRoutePath } from '@/utils/routes/generateRoutePath'

import { sendAuthCode } from './actions/sendAuthCode'

const schema = z.object({
  email: z.string().email({
    message: 'Email inválido',
  }),
})

export default function Login() {
  const router = useRouter()

  const form = useForm<z.infer<typeof schema>>({
    defaultValues: {
      email: '',
    },
    resolver: zodResolver(schema),
  })
  const { handleSubmit } = form

  const { toast } = useToast()

  const action: () => void = handleSubmit(async (data) => {
    const { email } = data

    const { message, success } = await sendAuthCode(data)

    if (!success) {
      trackLoginCodeSendFailed('email')
      toast({
        title: 'Algo deu errado',
        description: message,
        variant: 'destructive',
        icon: AlertCircle,
      })
      return
    }

    trackLoginCodeSent('email')
    toast({
      title: message,
      icon: Info,
    })

    router.push(
      generateRoutePath('verifyCode', {
        query: {
          email,
        },
      }),
    )
  })

  return (
    <Form {...form}>
      <form className="flex w-full flex-col items-center gap-4" action={action}>
        <FormField
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <FormItem className="w-full">
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  placeholder="Entre com email"
                  variant={fieldState.error && 'error'}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full gap-2"
          variant="accent"
          disabled={form.buttonDisabled}
        >
          Entrar
        </Button>
        <span className="text-sm text-system-text-primary">Ou</span>
        <Button variant="secondary" type="button" className="w-full" asChild>
          <Link href={generateRoutePath('loginByRegistration')}>
            Acessar via Matrícula
          </Link>
        </Button>
      </form>
    </Form>
  )
}
